import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  Upload,
  ImagePlus,
  Loader2,
  Sparkles,
  RotateCcw,
  DoorOpen,
  DoorClosed,
  Info,
  Move,
  AlertCircle,
  Crosshair,
  X,
} from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { doorStyles, demoFacade } from "@/content/doorStyles";
import { interiorView } from "@/content/designs";
import { waLink, visualizerCategories } from "@/content/site";
import { Reveal, SectionHead, WhatsAppIcon } from "@/components/apex/primitives";
import DoorSurface from "@/components/apex/DoorSurface";
import DesignThumb from "@/components/apex/DesignThumb";
import { quadToMatrix3d } from "@/components/apex/homography";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_EDGE = 1400;
const HOME_LIMIT = 18;
const H_MIN = 2;
const H_MAX = 9;
const W_MIN = 1.4;
const W_MAX = 2.5;
/* نتيجة الكشف المسبق للواجهة النموذجية (زوايا الفتحة بالمنظور الحقيقي) */
const DEMO_QUAD = [
  { x: 0.279, y: 0.151 },
  { x: 0.54, y: 0.149 },
  { x: 0.569, y: 0.975 },
  { x: 0.282, y: 0.982 },
];
const quadToBox = (q) => {
  const xs = q.map((p) => p.x);
  const ys = q.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
};
const DEMO_BOX = quadToBox(DEMO_QUAD);

const clamp = (v, a, b) => Math.min(Math.max(v, a), b);

const Visualizer = () => {
  const { t, isRtl } = useLang();
  const reduce = useReducedMotion();
  const v = t.viz;

  const [facade, setFacade] = useState(null); // {src, ratio}
  const [status, setStatus] = useState("idle"); // idle | detecting | auto | manual | error
  const [designId, setDesignId] = useState(doorStyles[0].id);
  const [heightM, setHeightM] = useState(3);
  const [widthM, setWidthM] = useState(1.6);
  const [calib, setCalib] = useState(1);
  const [baseScale, setBaseScale] = useState(0.6); // جزء من ارتفاع الصورة لكل متر
  const [anchor, setAnchor] = useState({ cx: 0.5, bottom: 0.9 });
  const [quad0, setQuad0] = useState(null); // زوايا الفتحة المكتشفة (نسبية للصورة)
  const [lighting, setLighting] = useState({ brightness: 1, tint: "rgba(0,0,0,0)" });

  /* مطابقة إضاءة الباب مع إضاءة الصورة: نقيس سطوع ولون الجدار حول الفتحة */
  useEffect(() => {
    if (!facade) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = 64;
        c.height = 64;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0, 64, 64);
        const { data } = ctx.getImageData(0, 8, 64, 48);
        let r = 0;
        let g = 0;
        let b = 0;
        const n = data.length / 4;
        for (let i = 0; i < data.length; i += 4) {
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
        }
        r /= n;
        g /= n;
        b /= n;
        const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
        /* صور التصاميم ملتقطة على جدار بسطوع ≈ 0.68 */
        const brightness = Math.min(1.15, Math.max(0.7, 1 + (lum - 0.68) * 0.9));
        setLighting({ brightness, tint: `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},0.22)` });
      } catch {
        setLighting({ brightness: 1, tint: "rgba(0,0,0,0)" });
      }
    };
    img.src = facade.src;
  }, [facade]);
  const [open, setOpen] = useState(false);
  const [studioOpen, setStudioOpen] = useState(false);
  const [openDeg, setOpenDeg] = useState(62); // زاوية الفتح اليدوية (خيار 3D)

  /* قفل تمرير الصفحة وإغلاق بـ Escape عندما يكون الاستوديو مفتوحًا */
  useEffect(() => {
    if (!studioOpen) return;
    const prev = document.body.style.overflow;
    const scrollY = window.scrollY;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    const onKey = (e) => e.key === "Escape" && setStudioOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
      /* إعادة الصفحة إلى موضعها قبل فتح الاستوديو (مع Lenis) */
      requestAnimationFrame(() => {
        if (window.__lenis) window.__lenis.scrollTo(scrollY, { immediate: true, force: true });
        else window.scrollTo({ top: scrollY, behavior: "instant" });
      });
    };
  }, [studioOpen]);

  const stageRef = useRef(null);
  const frameRef = useRef(null);
  const anchorRef = useRef(null);
  const fileRef = useRef(null);
  const [stageSize, setStageSize] = useState({ w: 1, h: 1 });
  const [frameW, setFrameW] = useState(0);
  const drag = useRef(null);

  const design = doorStyles.find((d) => d.id === designId) || doorStyles[0];

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setStageSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [facade]);

  /* قياس عرض الحاوية لعرض الصورة كاملة بنسبتها الحقيقية دون أي قصّ */
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setFrameW(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [facade]);

  const stageW = useMemo(() => {
    if (!facade || !frameW) return 0;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    return Math.min(frameW, vh * 0.74 * facade.ratio);
  }, [facade, frameW]);

  const ar = stageSize.w / stageSize.h || 1;
  const scale = baseScale * calib;
  const rect = useMemo(() => {
    const h = clamp(heightM * scale, 0.05, 1.6);
    const w = clamp((widthM * scale) / ar, 0.02, 1.2);
    return {
      w,
      h,
      left: anchor.cx - w / 2,
      top: anchor.bottom - h,
    };
  }, [heightM, widthM, scale, ar, anchor]);

  /* الزوايا الحيّة: الرباعي المكتشف مُسقَطًا داخل المستطيل الحالي (يتبع السحب والمقاسات) */
  const liveQuad = useMemo(() => {
    const corners = [
      { x: rect.left, y: rect.top },
      { x: rect.left + rect.w, y: rect.top },
      { x: rect.left + rect.w, y: rect.top + rect.h },
      { x: rect.left, y: rect.top + rect.h },
    ];
    if (!quad0) return corners;
    const b = quadToBox(quad0);
    return quad0.map((p) => ({
      x: rect.left + ((p.x - b.x) / b.w) * rect.w,
      y: rect.top + ((p.y - b.y) / b.h) * rect.h,
    }));
  }, [rect, quad0]);

  const warp = useMemo(() => {
    if (!quad0) return "none";
    const W = stageSize.w;
    const H = stageSize.h;
    const rel = liveQuad.map((p) => ({ x: (p.x - rect.left) * W, y: (p.y - rect.top) * H }));
    return quadToMatrix3d(rect.w * W, rect.h * H, rel);
  }, [liveQuad, quad0, rect, stageSize]);

  /* ---------------- رفع الصورة ---------------- */
  const loadFile = useCallback(async (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setOpen(false);
    setStatus("detecting");

    const dataUrl = await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = rej;
      fr.readAsDataURL(file);
    }).catch(() => null);
    if (!dataUrl) {
      setStatus("error");
      return;
    }

    const img = new Image();
    img.src = dataUrl;
    await new Promise((res) => {
      img.onload = res;
      img.onerror = res;
    });
    if (!img.naturalWidth) {
      setStatus("error");
      return;
    }

    const k = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const cw = Math.round(img.naturalWidth * k);
    const chh = Math.round(img.naturalHeight * k);
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = chh;
    canvas.getContext("2d").drawImage(img, 0, 0, cw, chh);
    const jpeg = canvas.toDataURL("image/jpeg", 0.86);

    setFacade({ src: jpeg, ratio: cw / chh });
    setQuad0(null);
    setAnchor({ cx: 0.5, bottom: 0.88 });
    setBaseScale(0.2);
    setCalib(1);

    await detect(jpeg, cw / chh);
  }, []);

  /* استدعاء خدمة الرؤية لتحديد فتحة الباب وتثبيت الدرفة عليها */
  const detect = async (src, aspect) => {
    setStatus("detecting");
    try {
      const body = src.startsWith("data:") ? { image_base64: src } : { image_url: src };
      const resp = await fetch(`${API}/visualizer/detect-door`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error("detect_failed");
      const data = await resp.json();
      if (data.found && data.box) {
        applyBox(data.box, aspect, data.quad || null, data.height_m || null);
        setStatus("auto");
      } else {
        setStatus("manual");
      }
    } catch {
      setStatus("manual");
    }
  };

  /* زر "ثبّت الباب تلقائيًا": يعيد الكشف على الصورة الحالية */
  const autoFit = async () => {
    if (!facade) return;
    setOpen(false);
    await detect(facade.src, facade.ratio);
  };

  const applyBox = (box, aspect, quad = null, estimatedHeightM = null) => {
    setQuad0(quad);
    const h = clamp(box.h, 0.08, 0.98);
    const wFrac = box.w * aspect; // عرض الفتحة بوحدة ارتفاع الصورة
    /* الارتفاع الحقيقي: تقدير الذكاء الاصطناعي من الصورة إن وُجد، وإلا ٣ أمتار */
    let hM = estimatedHeightM ? clamp(estimatedHeightM, H_MIN, H_MAX) : 3;
    let s = h / hM;
    let wM = wFrac / s;
    if (wM < W_MIN || wM > W_MAX) {
      wM = clamp(wM, W_MIN, W_MAX);
      s = wFrac / wM;
      hM = clamp(h / s, H_MIN, H_MAX);
    }
    setBaseScale(s);
    setCalib(1);
    setHeightM(hM);
    setWidthM(wM);
    setAnchor({ cx: clamp(box.x + box.w / 2, 0.05, 0.95), bottom: clamp(box.y + h, 0.1, 1) });
  };

  const useDemo = () => {
    setOpen(false);
    setFacade({ src: demoFacade, ratio: 1500 / 2000 });
    applyBox(DEMO_BOX, 1500 / 2000, DEMO_QUAD);
    setStatus("auto");
  };

  const reset = () => {
    setFacade(null);
    setQuad0(null);
    setStatus("idle");
    setOpen(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  /* ---------------- سحب الباب ---------------- */
  const onPointerDown = (e) => {
    if (e.button === 1 || e.button === 2) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = {
      id: e.pointerId,
      sx: e.clientX,
      sy: e.clientY,
      cx: anchor.cx,
      bottom: anchor.bottom,
      moved: 0,
    };
  };

  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dx = e.clientX - d.sx;
    const dy = e.clientY - d.sy;
    d.moved = Math.max(d.moved, Math.abs(dx) + Math.abs(dy));
    if (d.moved < 3) return;
    setAnchor({
      cx: clamp(d.cx + dx / stageSize.w, 0.04, 0.96),
      bottom: clamp(d.bottom + dy / stageSize.h, 0.12, 1.02),
    });
  };

  const onPointerUp = (e) => {
    const d = drag.current;
    drag.current = null;
    if (d && d.moved < 4) setOpen((o) => !o);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const onKeyDown = (e) => {
    const step = e.shiftKey ? 0.04 : 0.01;
    const map = {
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
    };
    if (map[e.key]) {
      e.preventDefault();
      const [mx, my] = map[e.key];
      setAnchor((a) => ({
        cx: clamp(a.cx + mx, 0.04, 0.96),
        bottom: clamp(a.bottom + my, 0.12, 1.02),
      }));
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen((o) => !o);
    }
  };

  /* ---------------- رسالة واتساب ---------------- */
  const waMessage = isRtl
    ? `مرحبًا APEX، جرّبت مجسّم المدخل على صورة واجهة بيتي.
التصميم: ${design.name_ar}
الارتفاع التقريبي: ${heightM.toFixed(2)} م
العرض التقريبي: ${widthM.toFixed(2)} م
أرغب بمعرفة الخيارات والسعر المناسب.`
    : `Hello APEX, I tried the entrance visualizer on a photo of my facade.
Design: ${design.name_en}
Approximate height: ${heightM.toFixed(2)} m
Approximate width: ${widthM.toFixed(2)} m
I would like to know the options and a suitable price.`;

  /* المفصل دائمًا على يمين الدرفة (اليد على الشمال) والفتح إلى الداخل */
  const hinge = "end";
  const openAngle = open ? -openDeg : 0;
  const [designFilter, setDesignFilter] = useState("all");
  const designFilters = [
    { id: "all", label: isRtl ? "الكل" : "All" },
    ...visualizerCategories.map((c) => ({ id: c.id, label: isRtl ? c.label_ar : c.label_en })),
  ];
  const visibleDesigns = doorStyles.filter((d) =>
    designFilter === "all" ? true : designFilter === "apex" ? d.source === "apex" : d.category === designFilter,
  );
  /* في الصفحة الرئيسية نعرض عددًا محدودًا ونترك الباقي للاستوديو أو لزر "عرض الكل" */
  const [showAllHome, setShowAllHome] = useState(false);
  const homeDesigns = showAllHome ? visibleDesigns : visibleDesigns.slice(0, HOME_LIMIT);
  /* اختيار تصميم من الشبكة: يظهر فورًا على الواجهة ونرجع للمسرح ليراه الزبون */
  const pickDesign = (id) => {
    setDesignId(id);
    if (facade) anchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="visualizer"
      data-testid="visualizer-section"
      className="relative overflow-hidden py-14 sm:py-20"
    >

      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
        <SectionHead align="center" label={v.label} title={v.title} body={v.body} />

        {/* ------------- شبكة التصاميم (اختيار التصميم يفتح الاستوديو) ------------- */}
        <Reveal delay={0.08}>
          <div className="mt-8 flex flex-wrap justify-center gap-2" role="group" aria-label={v.designsLabel}>
            {designFilters.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setDesignFilter(f.id)}
                aria-pressed={designFilter === f.id}
                data-testid={`visualizer-grid-filter-${f.id}`}
                className={`ap-filter ${designFilter === f.id ? "ap-filter-on" : ""}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="mt-6 grid grid-cols-3 gap-3 sm:grid-cols-4 sm:gap-4 lg:grid-cols-6" data-testid="visualizer-design-grid">
          {homeDesigns.map((d, i) => (
            <Reveal key={d.id} delay={Math.min(i, 11) * 0.03}>
              <button
                type="button"
                onClick={() => {
                  setDesignId(d.id);
                  setStudioOpen(true);
                }}
                data-testid={`visualizer-pick-${d.id}`}
                className="ap-card ap-card-hover group block w-full p-2 text-start"
              >
                <span className="ap-img relative block aspect-[3/4] w-full">
                  <DesignThumb item={d} alt={isRtl ? d.name_ar : d.name_en} />
                  <span className="absolute inset-x-2 bottom-2 rounded-full bg-[var(--yellow)] py-1.5 text-center text-[11px] font-semibold text-[var(--charcoal)] opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    {v.tryOnFacade}
                  </span>
                </span>
                <span className="mt-2 block truncate text-[12.5px] font-semibold text-[var(--text)]">
                  {isRtl ? d.name_ar : d.name_en}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
        {visibleDesigns.length > HOME_LIMIT && !showAllHome && (
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={() => setShowAllHome(true)}
              data-testid="visualizer-show-all"
              className="ap-btn-outline"
            >
              {v.showAll} ({visibleDesigns.length})
            </button>
          </div>
        )}

        <AnimatePresence>
        {studioOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          role="dialog"
          aria-modal="true"
          aria-label={v.title}
          data-testid="visualizer-studio"
          data-lenis-prevent
          className="ap-studio fixed inset-0 z-[90] overflow-y-auto"
        >
          {/* ------------- الشريط العلوي ------------- */}
          <div className="ap-studio-bar sticky top-0 z-30">
            <div className="mx-auto flex max-w-[1180px] items-center justify-between gap-4 px-4 py-3 sm:px-8">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--yellow)]">{v.studioLabel}</p>
                <p className="mt-0.5 flex items-center gap-2 text-[14px] font-semibold text-white sm:text-base">
                  <span className="truncate">{v.studioTitle}</span>
                  <span
                    className="hidden shrink-0 rounded-full bg-white/10 px-3 py-1 text-[12px] font-medium text-white/80 sm:inline-block"
                    data-testid="visualizer-design-name"
                  >
                    {isRtl ? design.name_ar : design.name_en}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => setStudioOpen(false)}
                aria-label={isRtl ? "إغلاق" : "Close"}
                data-testid="visualizer-studio-close"
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white text-[var(--charcoal)] shadow-lg transition-transform hover:scale-105"
              >
                <X className="h-5 w-5" strokeWidth={2.2} />
              </button>
            </div>
          </div>

          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto max-w-[1180px] px-4 pb-32 pt-5 sm:px-8 sm:pt-8"
          >
            {/* ------------- المسرح ------------- */}
            <div ref={anchorRef} className="scroll-mt-24">
              {!facade ? (
                <div
                  className="ap-studio-card flex flex-col items-center justify-center gap-6 px-6 py-16 text-center sm:py-20"
                  data-testid="visualizer-empty"
                >
                  <span className="grid h-20 w-20 place-items-center rounded-full bg-[var(--yellow)] text-[var(--charcoal)] shadow-[0_0_60px_-10px_rgba(245,197,24,0.8)]">
                    <ImagePlus className="h-8 w-8" strokeWidth={1.4} />
                  </span>
                  <div className="max-w-md">
                    <p className="ap-display text-2xl text-white sm:text-3xl">{v.emptyTitle}</p>
                    <p className="mt-3 text-[14px] leading-relaxed text-white/60">{v.emptyBody}</p>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      data-testid="visualizer-upload"
                      className="ap-studio-btn ap-studio-btn-primary"
                    >
                      <Upload className="h-4 w-4" strokeWidth={1.8} />
                      {v.upload}
                    </button>
                    <button type="button" onClick={useDemo} data-testid="visualizer-demo" className="ap-studio-btn">
                      {v.demo}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative" ref={frameRef}>
                  <div
                    ref={stageRef}
                    className="relative mx-auto select-none overflow-hidden rounded-[1.5rem] bg-[#111] shadow-[0_40px_120px_-40px_rgba(245,197,24,0.35),0_30px_60px_-30px_rgba(0,0,0,0.9)]"
                    style={{
                      width: stageW ? `${stageW}px` : "100%",
                      height: stageW ? `${stageW / facade.ratio}px` : "auto",
                      aspectRatio: String(facade.ratio),
                    }}
                    data-testid="visualizer-stage"
                  >
                    <img
                      src={facade.src}
                      alt={v.facadeAlt}
                      draggable="false"
                      className="absolute inset-0 h-full w-full object-fill"
                    />

                    {/* طبقة المسح أثناء الكشف */}
                    {status === "detecting" && (
                      <div
                        className="pointer-events-none absolute inset-0 z-20 grid place-items-center bg-[rgba(21,21,21,0.45)]"
                        data-testid="visualizer-scanning"
                        aria-hidden="true"
                      >
                        <motion.span
                          className="absolute inset-x-0 h-[2px] bg-[var(--yellow)] shadow-[0_0_24px_4px_rgba(245,197,24,0.6)]"
                          animate={{ top: ["4%", "96%", "4%"] }}
                          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
                        />
                        <span className="flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-[13px] font-semibold text-[var(--text)] shadow-lg">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {v.autoFit}…
                        </span>
                      </div>
                    )}

                    {/* ظل أرضي */}
                    <div
                      className="pointer-events-none absolute transition-opacity duration-700"
                      style={{
                        left: `${(Math.min(liveQuad[3].x, liveQuad[2].x) - 0.02) * 100}%`,
                        top: `${(Math.max(liveQuad[2].y, liveQuad[3].y) - 0.012) * 100}%`,
                        width: `${(Math.abs(liveQuad[2].x - liveQuad[3].x) + 0.04) * 100}%`,
                        height: `${Math.max(rect.h * 0.07, 0.02) * 100}%`,
                        background:
                          "radial-gradient(60% 100% at 50% 0%, rgba(0,0,0,0.55), rgba(0,0,0,0))",
                        opacity: open ? 0.9 : 0.5,
                        filter: "blur(3px)",
                      }}
                      aria-hidden="true"
                    />

                    {/* طبقة الإسقاط: تُطابق الفتحة الحقيقية بمنظورها (homography) */}
                    <div
                      className="absolute"
                      data-testid="visualizer-warp"
                      style={{
                        left: `${rect.left * 100}%`,
                        top: `${rect.top * 100}%`,
                        width: `${rect.w * 100}%`,
                        height: `${rect.h * 100}%`,
                        transform: warp,
                        transformOrigin: "0 0",
                        perspective: "1400px",
                      }}
                    >
                      {/* داخل البيت خلف الدرفة */}
                      <div
                        className="pointer-events-none absolute inset-0 overflow-hidden transition-opacity duration-700"
                        style={{ opacity: open ? 1 : 0 }}
                        aria-hidden="true"
                        data-testid="visualizer-interior"
                      >
                        <img
                          src={interiorView}
                          alt=""
                          className="absolute inset-0 h-full w-full object-cover"
                          style={{ objectPosition: "50% 60%", transform: "scale(1.08)" }}
                        />
                        <span
                          className="absolute inset-0"
                          style={{
                            boxShadow: "inset 0 0 60px rgba(0,0,0,0.75), inset 0 0 0 2px rgba(0,0,0,0.6)",
                            background: "linear-gradient(90deg, rgba(0,0,0,0.15), rgba(0,0,0,0) 30%, rgba(0,0,0,0) 70%, rgba(0,0,0,0.45))",
                          }}
                        />
                      </div>

                      {/* درفة الباب */}
                      <motion.button
                        type="button"
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={() => (drag.current = null)}
                        onKeyDown={onKeyDown}
                        aria-pressed={open}
                        aria-label={open ? v.closeDoor : v.openDoor}
                        data-testid="visualizer-door"
                        className="absolute inset-0 cursor-grab active:cursor-grabbing"
                        style={{
                          touchAction: "none",
                          transformStyle: "preserve-3d",
                          transformOrigin: hinge === "start" ? "15% 50%" : "85% 50%",
                          overflow: "hidden",
                        }}
                        animate={{
                          rotateY: openAngle,
                          boxShadow: open
                            ? "0 30px 60px -20px rgba(0,0,0,0.85)"
                            : "0 6px 18px -10px rgba(0,0,0,0.7)",
                        }}
                        transition={
                          reduce
                            ? { duration: 0 }
                            : { type: "spring", stiffness: 42, damping: 14, mass: 1.1 }
                        }
                      >
                        <div className="absolute inset-0" style={{ filter: `brightness(${lighting.brightness})` }}>
                          <DoorSurface design={design} hingeSide={hinge} />
                        </div>
                        <span
                          className="pointer-events-none absolute inset-0"
                          style={{ background: lighting.tint, mixBlendMode: "soft-light" }}
                          aria-hidden="true"
                        />
                        {/* لمعة تنزلق على السطح أثناء الفتح والإغلاق */}
                        <motion.span
                          className="pointer-events-none absolute inset-0"
                          style={{
                            background:
                              "linear-gradient(105deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.22) 48%, rgba(255,255,255,0.05) 52%, rgba(255,255,255,0) 70%)",
                            backgroundSize: "250% 100%",
                            mixBlendMode: "screen",
                          }}
                          animate={{ backgroundPosition: open ? "100% 0%" : "0% 0%" }}
                          transition={reduce ? { duration: 0 } : { duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
                          aria-hidden="true"
                        />
                        <span
                          className="pointer-events-none absolute inset-0 transition-opacity duration-700"
                          style={{
                            background:
                              hinge === "start"
                                ? "linear-gradient(90deg, rgba(0,0,0,0.45), rgba(0,0,0,0) 60%)"
                                : "linear-gradient(270deg, rgba(0,0,0,0.45), rgba(0,0,0,0) 60%)",
                            opacity: open ? 1 : 0,
                          }}
                          aria-hidden="true"
                        />
                      </motion.button>
                      {/* حافة الإطار لتثبيت الباب بصريًا داخل الفتحة */}
                      <span
                        className="pointer-events-none absolute inset-0"
                        style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.35), inset 0 2px 6px rgba(0,0,0,0.35)" }}
                        aria-hidden="true"
                      />
                    </div>
                  </div>

                  {/* شريط أدوات المسرح */}
                  <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={autoFit}
                      disabled={status === "detecting"}
                      data-testid="visualizer-autofit"
                      className="ap-studio-btn ap-studio-btn-primary"
                    >
                      {status === "detecting" ? (
                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                      ) : (
                        <Crosshair className="h-4 w-4" strokeWidth={1.8} />
                      )}
                      {v.autoFit}
                    </button>
                    <button
                      type="button"
                      onClick={() => setOpen((o) => !o)}
                      data-testid="visualizer-toggle"
                      aria-pressed={open}
                      className="ap-studio-btn"
                    >
                      {open ? (
                        <DoorClosed className="h-4 w-4" strokeWidth={1.6} />
                      ) : (
                        <DoorOpen className="h-4 w-4" strokeWidth={1.6} />
                      )}
                      {open ? v.closeDoor : v.openDoor}
                    </button>
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      data-testid="visualizer-change"
                      className="ap-studio-btn"
                    >
                      <Upload className="h-4 w-4" strokeWidth={1.6} />
                      {v.change}
                    </button>
                    <button type="button" onClick={reset} data-testid="visualizer-reset" className="ap-studio-btn">
                      <RotateCcw className="h-4 w-4" strokeWidth={1.6} />
                      {v.reset}
                    </button>
                  </div>
                  <p className="mt-3 flex items-center justify-center gap-2 text-[12px] text-white/50">
                    <Move className="h-3.5 w-3.5" strokeWidth={1.6} />
                    {v.dragHint}
                  </p>
                </div>
              )}

              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={(e) => loadFile(e.target.files?.[0])}
                className="sr-only"
                data-testid="visualizer-file-input"
                aria-label={v.upload}
              />

              {/* حالة الكشف */}
              {status !== "idle" && (
                <div
                  data-testid="visualizer-status"
                  className="mx-auto mt-4 flex max-w-2xl items-start gap-2.5 rounded-2xl border border-white/8 bg-white/5 px-4 py-3 text-[13px] leading-relaxed text-white/70"
                >
                  {status === "detecting" && (
                    <>
                      <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-[var(--yellow)]" />
                      {v.detecting}
                    </>
                  )}
                  {status === "auto" && (
                    <>
                      <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[var(--yellow)]" strokeWidth={1.6} />
                      {v.autoFound}
                    </>
                  )}
                  {status === "manual" && (
                    <>
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--yellow)]" strokeWidth={1.6} />
                      {v.manualHint}
                    </>
                  )}
                  {status === "error" && (
                    <>
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#ff6b63]" strokeWidth={1.6} />
                      {v.loadError}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ------------- المقاسات ------------- */}
            {facade && (
              <div className="ap-studio-card mt-6 p-5 sm:p-7" data-testid="visualizer-dims">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="ap-display text-lg text-white">{v.dimsTitle}</h3>
                  <p className="text-[12.5px] text-white/50">{v.dimsHint}</p>
                </div>
                <div className="mt-5 grid gap-6 sm:grid-cols-2 sm:gap-8">
                  <Slider
                    id="viz-h"
                    label={v.heightLabel}
                    value={heightM}
                    min={H_MIN}
                    max={H_MAX}
                    step={0.05}
                    unit={v.meter}
                    onChange={setHeightM}
                    testid="visualizer-height"
                  />
                  <Slider
                    id="viz-w"
                    label={v.widthLabel}
                    value={widthM}
                    min={W_MIN}
                    max={W_MAX}
                    step={0.01}
                    unit={v.meter}
                    onChange={setWidthM}
                    testid="visualizer-width"
                  />
                </div>
              </div>
            )}

            {/* ------------- كل التصاميم ------------- */}
            <section className="mt-10" data-testid="visualizer-panel">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--yellow)]">{v.designsLabel}</p>
                  <h3 className="ap-display mt-1 text-2xl text-white sm:text-3xl">
                    {isRtl ? design.name_ar : design.name_en}
                  </h3>
                  <p className="mt-2 max-w-xl text-[13px] leading-relaxed text-white/55">{v.studioHint}</p>
                </div>
                <span className="rounded-full bg-white/8 px-3 py-1 text-[12px] text-white/60">
                  {visibleDesigns.length} {v.designsCount}
                </span>
              </div>

              <div
                className="mt-5 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                role="group"
                aria-label={v.designsLabel}
              >
                {designFilters.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setDesignFilter(f.id)}
                    aria-pressed={designFilter === f.id}
                    data-testid={`visualizer-filter-${f.id}`}
                    className={`ap-studio-chip shrink-0 ${designFilter === f.id ? "ap-studio-chip-on" : ""}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div
                className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-4 sm:gap-4 lg:grid-cols-6"
                data-testid="visualizer-design-strip"
              >
                {visibleDesigns.map((d, i) => {
                  const on = d.id === designId;
                  return (
                    <motion.button
                      key={d.id}
                      type="button"
                      initial={{ opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.45, delay: Math.min(i, 17) * 0.025, ease: [0.16, 1, 0.3, 1] }}
                      onClick={() => pickDesign(d.id)}
                      aria-pressed={on}
                      title={isRtl ? d.name_ar : d.name_en}
                      data-testid={`visualizer-design-${d.id}`}
                      className={`ap-studio-tile group text-start ${on ? "ap-studio-tile-on" : ""}`}
                    >
                      <span className="relative block aspect-[3/4] w-full overflow-hidden">
                        <DesignThumb item={d} alt={isRtl ? d.name_ar : d.name_en} />
                        {on && (
                          <span className="absolute start-2 top-2 rounded-full bg-[var(--yellow)] px-2 py-0.5 text-[10px] font-bold text-[var(--charcoal)]">
                            ✓
                          </span>
                        )}
                      </span>
                      <span className="block truncate px-2.5 py-2 text-[12px] font-semibold text-white/85">
                        {isRtl ? d.name_ar : d.name_en}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </section>

            {/* ------------- خيارات ثلاثية الأبعاد ------------- */}
            <details className="ap-studio-card mt-8 px-5 py-4" data-testid="visualizer-calib-details">
              <summary className="cursor-pointer text-[13.5px] font-semibold text-white/85">{v.options3d}</summary>
              <div className="grid gap-6 pt-5 sm:grid-cols-2 sm:gap-8">
                <Slider
                  id="viz-deg"
                  label={v.openAngleLabel}
                  value={openDeg}
                  min={20}
                  max={95}
                  step={1}
                  unit="°"
                  onChange={(val) => {
                    setOpenDeg(val);
                    setOpen(true);
                  }}
                  testid="visualizer-open-angle"
                />
                <Slider
                  id="viz-c"
                  label={v.calibLabel}
                  value={calib}
                  min={0.55}
                  max={1.7}
                  step={0.01}
                  unit="×"
                  onChange={setCalib}
                  testid="visualizer-calib"
                  hint={v.calibHint}
                />
              </div>
            </details>

            <p className="mt-6 flex items-start gap-2 text-[11.5px] leading-relaxed text-white/45">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--yellow)]" strokeWidth={1.6} />
              {v.privacy}
            </p>
            <p className="mt-2 text-[11.5px] leading-relaxed text-white/35">{v.disclaimer}</p>
          </motion.div>

          {/* ------------- شريط سفلي ثابت: واتساب ------------- */}
          <div className="ap-studio-bar-bottom fixed inset-x-0 bottom-0 z-30 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-6 sm:px-8">
            <div className="mx-auto flex max-w-[1180px] items-center justify-between gap-3">
              {facade && (
                <button
                  type="button"
                  onClick={() => anchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  data-testid="visualizer-back-to-stage"
                  className="ap-studio-btn shrink-0 !h-auto min-h-[2.75rem] py-2 text-center !text-[12.5px] sm:!text-[13px]"
                >
                  {v.backToTop}
                </button>
              )}
              <a
                href={waLink(waMessage)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visualizer-whatsapp"
                className="ap-btn-wa w-full sm:ms-auto sm:w-auto sm:min-w-[320px]"
              >
                <WhatsAppIcon className="h-5 w-5" />
                {v.send}
              </a>
            </div>
          </div>
        </motion.div>
        )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const Slider = ({ id, label, value, min, max, step, unit, onChange, testid, hint }) => (
  <div>
    <div className="flex items-baseline justify-between gap-3">
      <label htmlFor={id} className="text-[13px] font-medium text-white/85">
        {label}
      </label>
      <span className="ap-mono text-sm font-semibold text-[var(--yellow)]" dir="ltr" data-testid={`${testid}-value`}>
        {step >= 1 ? Math.round(value) : value.toFixed(2)} {unit}
      </span>
    </div>
    <input
      id={id}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      data-testid={testid}
      className="ap-range ap-studio-range mt-3"
    />
    {hint && <p className="mt-2 text-[11px] text-white/40">{hint}</p>}
  </div>
);

export default Visualizer;
